import { useEffect, useState } from 'react'
import { Sparkles } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import {
  getCardContent,
  getNodePage,
  getVaultFile,
  getVaultFileObjectUrl,
  type VaultNode,
  type VaultPagePayload,
} from '@/lib/api'
import { useVaultStore } from '@/stores/useVaultStore'
import { MarkdownVaultAnchor, MarkdownVaultImage, vaultUrlTransform } from '@/lib/vaultLink'
import { useAutoRefresh } from './useAutoRefresh'
import { ExtTag, formatWhen, groupChildren, NodeIcon, nodeTextClass, StatusPill, type ChildGroup } from './vaultUi'
import { VaultCardEditor } from './VaultCardEditor'
import { WorkstreamCards } from './WorkstreamCards'
import './vault-editor.css'

export function VaultNodePage() {
  const selectedNodeId = useVaultStore((s) => s.selectedNodeId)
  const select = useVaultStore((s) => s.select)
  const [data, setData] = useState<VaultPagePayload | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!selectedNodeId) {
      setData(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    getNodePage(selectedNodeId)
      .then((d) => !cancelled && setData(d))
      .catch((e) => {
        if (cancelled) return
        // A 404 means the selected node no longer exists (archived / moved /
        // deleted) — clear the stale selection so we stop retrying a dead id
        // and fall back to "Nothing selected". A 500 (transient, e.g. pool
        // contention) is left as an error so we don't drop a valid selection.
        if (/HTTP 404/.test(String(e))) {
          select(null)
          return
        }
        setError(String(e))
      })
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [selectedNodeId, select])

  // Background freshness: silently re-pull the open node's page (children,
  // statuses, summary chips) so watcher/Concierge changes appear without a
  // manual reload. No loading flash; the card editor is path-keyed so this
  // never disturbs in-flight edits.
  useAutoRefresh(() => {
    if (!selectedNodeId) return
    getNodePage(selectedNodeId)
      .then((d) => setData(d))
      // Drop a selection that's gone (404); ignore transient errors.
      .catch((e) => { if (/HTTP 404/.test(String(e))) select(null) })
  })

  if (!selectedNodeId) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-1 px-6 text-center">
        <div className="text-sm text-muted-foreground">Nothing selected</div>
        <div className="text-xs text-muted-foreground/70">Pick a domain, workstream, block, or card from the tree.</div>
      </div>
    )
  }
  if (error) return <div className="p-6 text-sm text-red-400">{error}</div>
  if (!data) return <div className="p-6 text-sm text-muted-foreground">{loading ? 'Loading…' : ''}</div>

  const { node, summary, children, breadcrumb } = data

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:px-8">
      {/* breadcrumb — ancestors are clickable */}
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
        <span className="rounded bg-muted px-1.5 py-0.5 uppercase tracking-wide">{node.node_type}</span>
        {breadcrumb.map((c, i) => (
          <span key={c.id} className="flex items-center gap-1.5">
            {i > 0 && <span className="opacity-50">/</span>}
            <button onClick={() => select(c.id)} className="transition-colors hover:text-foreground hover:underline">
              {c.name}
            </button>
          </span>
        ))}
        {node.is_archived && <span className="ml-1 opacity-70">📦 archived</span>}
      </div>

      {/* header */}
      <div className="mb-4 flex items-start justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">{node.name}</h1>
        {node.node_type === 'block' &&
          (node.is_archived ? (
            <span className="shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              archived
            </span>
          ) : (
            <StatusPill status={node.calendar_status} wrapped={node.block_wrapped} />
          ))}
      </div>

      <SummaryStrip node={node} summary={summary} />

      {node.node_type === 'workstream' && <WorkstreamConcierge path={node.path} />}

      {node.node_type === 'block' && node.output_intent && (
        <div className="mb-4 rounded-md border border-border bg-muted/40 p-3 text-[13px] text-foreground/90">
          <span className="text-muted-foreground">Output · </span>
          {node.output_intent}
        </div>
      )}

      {node.node_type === 'card' &&
        (node.ext === '.md' ? <VaultCardEditor path={node.path} /> : <CardViewer node={node} />)}

      {node.node_type === 'block' && <BlockBody path={node.path} />}

      {node.node_type === 'workstream' ? (
        <>
          {children.length > 0 && (
            <div className="mt-6 flex flex-col gap-4">
              {groupChildren('workstream', children)
                .filter((g) => g.key !== 'cards' && g.nodes.length > 0)
                .map((g) => (
                  <ChildGroupSection key={g.key} group={g} onSelect={select} />
                ))}
            </div>
          )}
          <WorkstreamCards nodeId={node.id} onSelect={select} />
        </>
      ) : (
        node.node_type !== 'card' &&
        children.length > 0 && (
          <div className="mt-6 flex flex-col gap-4">
            {groupChildren(node.node_type, children)
              .filter((g) => g.nodes.length > 0)
              .map((g) => (
                <ChildGroupSection key={g.key} group={g} onSelect={select} />
              ))}
          </div>
        )
      )}
    </div>
  )
}

function ChildRow({ node, onSelect }: { node: VaultNode; onSelect: (id: string) => void }) {
  return (
    <button
      onClick={() => onSelect(node.id)}
      className={`flex items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors hover:bg-muted/60 ${nodeTextClass(node.node_type)}`}
    >
      <NodeIcon type={node.node_type} />
      <span className="truncate">{node.name}</span>
      <span className="ml-auto flex shrink-0 items-center gap-2">
        {node.node_type === 'block' && (
          <>
            {formatWhen(node) && <span className="text-[10px] text-muted-foreground">{formatWhen(node)}</span>}
            {!node.is_archived && <StatusPill status={node.calendar_status} wrapped={node.block_wrapped} />}
          </>
        )}
        {node.node_type === 'card' && <ExtTag ext={node.ext} />}
        {node.is_archived && <span className="text-[10px] opacity-60">📦</span>}
      </span>
    </button>
  )
}

function ChildGroupSection({ group, onSelect }: { group: ChildGroup; onSelect: (id: string) => void }) {
  const list = (
    <div className="flex flex-col divide-y divide-border overflow-hidden rounded-md border border-border">
      {group.nodes.map((c) => (
        <ChildRow key={c.id} node={c} onSelect={onSelect} />
      ))}
    </div>
  )
  if (group.collapsed) {
    return (
      <details className="group/sec">
        <summary className="mb-2 flex cursor-pointer list-none items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground">
          <span className="transition-transform group-open/sec:rotate-90">▸</span>
          {group.label}
          <span className="font-normal text-muted-foreground/70">{group.nodes.length}</span>
        </summary>
        {list}
      </details>
    )
  }
  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {group.label}
        <span className="font-normal text-muted-foreground/70">{group.nodes.length}</span>
      </div>
      {list}
    </div>
  )
}

/** The workstream's CONCIERGE.md — the project-memory layer (agent-owned
 * narrative Concierge loads on every project turn). Surfaced read-only so it's
 * visible where the work lives, not buried as a card. */
function WorkstreamConcierge({ path }: { path: string }) {
  const [content, setContent] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoaded(false)
    setContent(null)
    getCardContent(`${path}/CONCIERGE.md`)
      .then((c) => {
        if (cancelled) return
        const agent = (c.managed ?? c.body ?? '').trim()
        setContent(agent || null)
        setLoaded(true)
      })
      .catch(() => {
        if (!cancelled) setLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [path])

  if (loaded && !content) return null

  // Collapsed by default — the agent notes can be long; the project page should
  // lead with status + blocks/cards. Click the header to expand.
  return (
    <details className="group/cc mb-4 rounded-md border border-indigo-500/20 bg-indigo-500/5 p-3">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-300/80">
        <span className="inline-block w-3 transition-transform group-open/cc:rotate-90">▸</span>
        <Sparkles size={11} />
        Concierge — what it knows
      </summary>
      <div className="mt-2">
        {content ? (
          <div className="vault-prose text-[13px]">
            <ReactMarkdown urlTransform={vaultUrlTransform} components={{ a: MarkdownVaultAnchor, img: MarkdownVaultImage }}>{content}</ReactMarkdown>
          </div>
        ) : (
          <div className="text-[12px] text-muted-foreground">loading…</div>
        )}
      </div>
    </details>
  )
}

const SECTION_ORDER = ['intent', 'reference', 'outcome', 'next', 'scaffold', 'calendar_notes'] as const
const SECTION_LABELS: Record<string, string> = {
  intent: 'Intent',
  reference: 'Reference',
  outcome: 'Outcome',
  next: 'Next',
  scaffold: 'Scaffold',
  calendar_notes: 'Calendar notes',
}

/** The block's own note: Concierge-owned sections (read-only) + the editable
 * user body. Sections come from the content endpoint's parsed fences. */
function BlockBody({ path }: { path: string }) {
  const [sections, setSections] = useState<Record<string, string> | null>(null)

  useEffect(() => {
    let cancelled = false
    setSections(null)
    getCardContent(path)
      .then((c) => !cancelled && setSections(c.sections))
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [path])

  // Keep the Concierge-owned sections (intent/reference/outcome/…) fresh as the
  // agent or watcher rewrites them. Read-only display, so always safe to swap.
  useAutoRefresh(() => {
    getCardContent(path)
      .then((c) => setSections(c.sections))
      .catch(() => {})
  })

  const present = sections ? SECTION_ORDER.filter((k) => sections[k]) : []

  return (
    <div className="mt-2">
      {present.map((k) => (
        <div key={k} className="mb-3 rounded-md border border-border bg-muted/20 p-3">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {SECTION_LABELS[k]}
          </div>
          <div className="vault-prose text-[14px]">
            <ReactMarkdown urlTransform={vaultUrlTransform} components={{ a: MarkdownVaultAnchor, img: MarkdownVaultImage }}>{sections![k]}</ReactMarkdown>
          </div>
        </div>
      ))}

      <div className="mt-4">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Notes</div>
        <VaultCardEditor path={path} />
      </div>
    </div>
  )
}

function SummaryStrip({ node, summary }: { node: VaultNode; summary: Record<string, number> }) {
  if (node.node_type === 'workstream') {
    const chips: { label: string; key: string; emphasize?: boolean }[] = [
      { label: 'needs attention', key: 'needs_attention', emphasize: true },
      { label: 'overdue', key: 'overdue' },
      { label: 'missing', key: 'missing' },
      { label: 'scheduled', key: 'scheduled' },
      { label: 'pending', key: 'pending' },
      { label: 'total', key: 'total' },
    ]
    return (
      <div className="mb-4 flex flex-wrap gap-2">
        {chips.map((c) => (
          <Stat key={c.key} label={c.label} value={summary[c.key] ?? 0} emphasize={c.emphasize} />
        ))}
      </div>
    )
  }
  if (node.node_type === 'domain') {
    return (
      <div className="mb-4 flex flex-wrap gap-2">
        <Stat label="active workstreams" value={summary.active_ws ?? 0} />
        <Stat label="archived" value={summary.archived_ws ?? 0} />
        <Stat label="active blocks" value={summary.active_blocks ?? 0} />
      </div>
    )
  }
  if (node.node_type === 'block') {
    return (
      <div className="mb-4 flex flex-wrap gap-2">
        <Stat label="cards" value={summary.card_count ?? 0} />
      </div>
    )
  }
  return null
}

function Stat({ label, value, emphasize }: { label: string; value: number; emphasize?: boolean }) {
  const hot = emphasize && value > 0
  return (
    <div
      className={`flex items-baseline gap-1.5 rounded-md border px-2.5 py-1 ${
        hot ? 'border-orange-500/40 bg-orange-500/10' : 'border-border bg-muted/40'
      }`}
    >
      <span className={`text-sm font-semibold ${hot ? 'text-orange-300' : 'text-foreground'}`}>{value}</span>
      <span className="text-[11px] text-muted-foreground">{label}</span>
    </div>
  )
}

function CardViewer({ node }: { node: VaultNode }) {
  const [url, setUrl] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [text, setText] = useState<string | null>(null)
  const [loadingText, setLoadingText] = useState(false)
  const isImage = (node.mime ?? '').startsWith('image/')
  const isPdf = node.ext === '.pdf'
  const renderable = isImage || isPdf
  const filename = `${node.name}${node.ext ?? ''}`

  // The authed object URL — powers img/pdf render + Open/Download (a plain link
  // to /raw can't carry the auth header, so we use the blob URL).
  useEffect(() => {
    let revoked: string | null = null
    let cancelled = false
    setUrl(null)
    setErr(null)
    getVaultFileObjectUrl(node.path)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u)
          return
        }
        revoked = u
        setUrl(u)
      })
      .catch((e) => !cancelled && setErr(String(e)))
    return () => {
      cancelled = true
      if (revoked) URL.revokeObjectURL(revoked)
    }
  }, [node.path])

  // For non-renderable types (docx/eml/txt/…), pull server-extracted text.
  useEffect(() => {
    if (renderable) {
      setText(null)
      return
    }
    let cancelled = false
    setLoadingText(true)
    setText(null)
    getVaultFile(node.path)
      .then((r) => {
        if (!cancelled) setText(r.kind === 'text' && r.content ? r.content : null)
      })
      .catch(() => !cancelled && setText(null))
      .finally(() => !cancelled && setLoadingText(false))
    return () => {
      cancelled = true
    }
  }, [node.path, renderable])

  if (err) return <div className="text-sm text-red-400">Couldn’t load file: {err}</div>
  if (renderable && !url) return <div className="text-sm text-muted-foreground">Loading file…</div>
  if (isImage && url) {
    return <img src={url} alt={node.name} className="max-h-[70vh] rounded-md border border-border" />
  }
  if (isPdf && url) {
    return <iframe src={url} title={node.name} className="h-[72vh] w-full rounded-md border border-border bg-white" />
  }

  // Everything else: a toolbar (Open / Download) + extracted text when available.
  return (
    <div className="overflow-hidden rounded-md border border-border">
      <div className="flex items-center gap-2 border-b border-border bg-muted/20 px-3 py-2 text-[12px]">
        <ExtTag ext={node.ext} />
        <span className="truncate text-muted-foreground">{filename}</span>
        <div className="ml-auto flex shrink-0 gap-2">
          {url && (
            <a href={url} target="_blank" rel="noreferrer" className="rounded border border-border px-2 py-0.5 text-muted-foreground transition-colors hover:text-foreground">
              Open
            </a>
          )}
          {url && (
            <a href={url} download={filename} className="rounded border border-border px-2 py-0.5 text-muted-foreground transition-colors hover:text-foreground">
              Download
            </a>
          )}
        </div>
      </div>
      <div className="p-3">
        {loadingText ? (
          <div className="text-[12px] text-muted-foreground">Reading…</div>
        ) : text ? (
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words text-[13px] leading-relaxed text-foreground/90">
            {text}
          </pre>
        ) : (
          <div className="text-[12px] text-muted-foreground">No inline preview for this type — use Open or Download.</div>
        )}
      </div>
    </div>
  )
}
